/* Tiaracle predictive search controller. Built 2026-08-11.
 *
 * Binds EVERY search bar in the theme to Shopify's native Predictive Search, so the header
 * bar, the compact sticky bar, the mobile header row, the homepage band and the search band
 * on the hub pages all behave identically. Any search form added later is picked up too:
 * the binding is by form shape, not by a hand-kept list of ids.
 *
 * Results are rendered server side by sections/tia-predictive-search.liquid through the
 * Section Rendering API, so money formatting, image transforms and escaping stay in Liquid.
 *
 * Deliberate choices worth keeping:
 *  - The panel is inserted as a SIBLING of the form, never inside it. Both
 *    `.tia-hdr__search form` and `.tia-hs form` set `overflow:hidden` to hide a stray strip
 *    of their own background, which would clip a dropdown rendered inside them.
 *  - One in-flight request at a time per input, cancelled by AbortController. Without this a
 *    slow early keystroke can resolve after a fast later one and paint stale results.
 *  - Results are cached per term for the life of the page, so backspacing is instant and
 *    costs no request.
 *  - The legacy `[data-predictive-search]` form that ships with the theme is skipped. It is
 *    hidden by the rebuilt header's CSS, and binding it too would open two dropdowns.
 */
(function () {
  'use strict';

  var SECTION = 'tia-predictive-search';
  var MIN = 2;
  var DEBOUNCE = 180;
  var TYPES = 'query,product,collection,article,page';
  var cache = Object.create(null);
  var seq = 0;
  // Only one bar may show results at a time. The page carries several search bars (header,
  // sticky header, homepage band, hub band) and the click-outside handler cannot close a
  // panel when the click lands in a DIFFERENT search form - that counts as "inside a search
  // form". Measured: focusing the homepage band with the header panel open left both open.
  var openOne = null;

  function esc(s) { return String(s == null ? '' : s); }

  function buildUrl(term) {
    return '/search/suggest'
      + '?q=' + encodeURIComponent(term)
      + '&resources[type]=' + TYPES
      // 6 is the largest per-type cap the section actually renders (products), so asking for
      // more just makes Shopify build rows that are thrown away. Measured over 5 terms:
      // limit 8 = 404ms average, limit 6 = 365ms, identical payload.
      + '&resources[limit]=6'
      + '&resources[limit_scope]=each'
      + '&resources[options][unavailable_products]=last'
      + '&section_id=' + SECTION;
  }

  function Search(form) {
    var input = form.querySelector('input[name="q"]');
    if (!input || input.type === 'hidden') return null;

    var host = form.parentNode;                 // .tia-hdr__search / .tia-hs / .tia-sb
    if (!host) return null;

    var panel = document.createElement('div');
    panel.className = 'tsr-panel';
    panel.hidden = true;
    panel.id = 'tsr-panel-' + (++seq);
    form.insertAdjacentElement('afterend', panel);

    // host must establish a containing block for the absolutely positioned panel
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var api = {
      form: form, input: input, panel: panel,
      ctrl: null, timer: 0, open: false, idx: -1, items: []
    };

    // ---- ARIA: the input is a combobox that owns the results listbox ----
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('autocomplete', 'off');
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');

    function collect() {
      api.items = Array.prototype.slice.call(panel.querySelectorAll('[data-tsr-item]'));
      api.items.forEach(function (el, i) { el.id = panel.id + '-o' + i; });
    }

    function highlight(n) {
      if (!api.items.length) return;
      if (api.idx > -1 && api.items[api.idx]) {
        api.items[api.idx].classList.remove('is-active');
        api.items[api.idx].setAttribute('aria-selected', 'false');
      }
      api.idx = n;
      if (n < 0) { input.removeAttribute('aria-activedescendant'); return; }
      var el = api.items[n];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    // On mobile the panel is position:fixed (see tia-search-styles for why), so BOTH its top
    // and the height it may use have to be measured, not guessed. The first cut used a CSS
    // `max-height: calc(100vh - 190px)`, which assumes the search bar sits near the top of the
    // screen. It does in the header; it does not in the homepage band, where the form is ~360px
    // down a 664px viewport and the panel ran 177px past the bottom edge.
    //
    // visualViewport is preferred over innerHeight because the on-screen keyboard shrinks the
    // visual viewport but not the layout viewport, and the keyboard is always up here.
    function place() {
      if (!api.open) return;
      if (window.innerWidth > 798) { panel.style.top = ''; panel.style.maxHeight = ''; return; }
      var r = form.getBoundingClientRect();
      var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      var top = Math.round(r.bottom + 6);
      panel.style.top = top + 'px';
      panel.style.maxHeight = Math.max(160, Math.round(vh - top - 10)) + 'px';
    }

    function show() {
      if (openOne && openOne !== api) openOne.hide();
      openOne = api;
      if (api.open) return;
      panel.hidden = false;
      api.open = true;
      input.setAttribute('aria-expanded', 'true');
      place();
    }

    function hide() {
      if (!api.open) return;
      panel.hidden = true;
      api.open = false;
      api.idx = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      if (openOne === api) openOne = null;
    }

    api.hide = hide;
    api.place = place;

    function paint(html) {
      panel.innerHTML = html;
      collect();
      api.idx = -1;
      if (panel.querySelector('[data-tsr]')) show(); else hide();
    }

    function run(term) {
      if (cache[term] !== undefined) { paint(cache[term]); return; }
      if (api.ctrl) api.ctrl.abort();
      api.ctrl = ('AbortController' in window) ? new AbortController() : null;
      panel.classList.add('is-loading');
      fetch(buildUrl(term), { signal: api.ctrl ? api.ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          panel.classList.remove('is-loading');
          if (!html) { hide(); return; }
          cache[term] = html;
          // the field may have moved on while this was in flight
          if (input.value.trim() === term) paint(html);
        })
        .catch(function (e) {
          panel.classList.remove('is-loading');
          if (!e || e.name !== 'AbortError') hide();
        });
    }

    function onType() {
      var term = input.value.trim();
      clearTimeout(api.timer);
      if (term.length < MIN) {
        if (api.ctrl) api.ctrl.abort();
        hide();
        return;
      }
      api.timer = setTimeout(function () { run(term); }, DEBOUNCE);
    }

    input.addEventListener('input', onType);
    input.addEventListener('focus', function () {
      if (input.value.trim().length >= MIN && panel.querySelector('[data-tsr]')) show();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (api.open) { e.stopPropagation(); hide(); } 
        return;
      }
      if (!api.open || !api.items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight((api.idx + 1) % api.items.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight((api.idx - 1 + api.items.length) % api.items.length); }
      else if (e.key === 'Enter') {
        // Enter with nothing highlighted must still submit the form normally
        if (api.idx > -1) { e.preventDefault(); window.location.href = api.items[api.idx].href; }
      } else if (e.key === 'Tab') { hide(); }
    });

    // pointer highlight keeps mouse and keyboard in sync
    panel.addEventListener('mousemove', function (e) {
      var it = e.target.closest ? e.target.closest('[data-tsr-item]') : null;
      if (it) { var i = api.items.indexOf(it); if (i > -1 && i !== api.idx) highlight(i); }
    });

    form.addEventListener('submit', function () { hide(); });
    return api;
  }

  var instances = [];

  function init() {
    // every search form, current and future, minus the theme's own hidden predictive form
    var forms = document.querySelectorAll('form[action*="/search"]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.__tsrBound) return;
      if (form.querySelector('[data-predictive-search]')) return;   // legacy theme form
      /* A form can point at /search and carry q without being a search BAR. The search page's
         sort form and its price-filter form both post to /search with a hidden q so the term
         survives the submit; binding those injected an empty results panel INSIDE the filter
         bar, which pushed the sort control onto its own row. Require a q the user can type in. */
      var qi = form.querySelector('input[name="q"]');
      if (!qi || qi.type === 'hidden') return;
      var api = Search(form);
      if (api) { form.__tsrBound = true; instances.push(api); }
    });
  }

  function hideAll() { instances.forEach(function (i) { i.hide && i.hide(); }); }
  function placeAll() { instances.forEach(function (i) { i.place && i.place(); }); }

  document.addEventListener('click', function (e) {
    var inside = e.target.closest && e.target.closest('form[action*="/search"], .tsr-panel');
    if (!inside) hideAll();
  });
  // The compact header changes height on scroll, so a docked mobile panel must follow it.
  window.addEventListener('resize', hideAll);
  window.addEventListener('scroll', placeAll, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', placeAll);
    window.visualViewport.addEventListener('scroll', placeAll);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // InstantClick swaps the document without a reload; layout/theme.liquid calls this after.
  window.tiaSearchInit = function () { instances.length = 0; init(); };
})();